import type { MessageDescriptor } from 'react-intl'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import LinePlayCircleIcon from 'LuxKit/Icons/line/LinePlayCircleIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineClochIcon from 'LuxKit/Icons/line/LineClochIcon'
import LineDoorIcon from 'LuxKit/Icons/line/LineDoorIcon'
import LineBagIcon from 'LuxKit/Icons/line/LineBagIcon'
import LineLuggageIcon from 'LuxKit/Icons/line/LineLuggageIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import { FlightsClassTypes } from 'constants/flight'

export interface CabinAmenity {
  icon: React.ComponentType
  message: MessageDescriptor
}

export interface CharterCabinContent {
  imageId: string
  inclusions: Array<CabinAmenity>
  baggage: Array<CabinAmenity>
}

export const CHARTER_CABIN_CONTENT: Record<string, CharterCabinContent> = {
  [FlightsClassTypes.ECONOMY]: {
    imageId: 'maldives-economy',
    inclusions: [
      {
        icon: LineSeatIcon,
        message: {
          id: 'F4yxnIGbGZ-HwUEN66LP',
          defaultMessage: 'Main cabin standard seat',
          description: 'Charter flight economy amenity: standard seat description',
        },
      },
      {
        icon: LinePlayCircleIcon,
        message: {
          id: 'jOMZWuWfYpzHHZnLuRO7',
          defaultMessage: 'Stream on-demand entertainment to your personal device',
          description: 'Charter flight amenity: in-flight entertainment streaming to personal device',
        },
      },
      {
        icon: LineRestaurantIcon,
        message: {
          id: 'BQRK_i0lCZoPiyXIpz2F',
          defaultMessage: 'Complimentary meal and drinks service',
          description: 'Charter flight amenity: complimentary meal and drinks',
        },
      },
    ],
    baggage: [
      {
        icon: LineBagIcon,
        message: {
          id: 'SBBJdZLWFogZDnxPBcZ6',
          defaultMessage: 'Carry-on 7kg',
          description: 'Charter flight standard carry-on baggage allowance',
        },
      },
      {
        icon: LineLuggageIcon,
        message: {
          id: 'dDAOzVfonIy_jZHM2Ry7',
          defaultMessage: 'Checked bag 23kg',
          description: 'Maldives charter flight standard checked baggage allowance',
        },
      },
    ],
  },
  [FlightsClassTypes.PREMIUM]: {
    imageId: 'maldives-economy-plus',
    inclusions: [
      {
        icon: LineSeatIcon,
        message: {
          id: 'bIUPAClOVQhzkpllLneO',
          defaultMessage: 'Extra legroom and generous seat recline for enhanced comfort',
          description: 'Charter flight economy plus amenity: enhanced seat comfort description',
        },
      },
      {
        icon: LinePlayCircleIcon,
        message: {
          id: 'jOMZWuWfYpzHHZnLuRO7',
          defaultMessage: 'Stream on-demand entertainment to your personal device',
          description: 'Charter flight amenity: in-flight entertainment streaming to personal device',
        },
      },
      {
        icon: LineRestaurantIcon,
        message: {
          id: 'BQRK_i0lCZoPiyXIpz2F',
          defaultMessage: 'Complimentary meal and drinks service',
          description: 'Charter flight amenity: complimentary meal and drinks',
        },
      },
      {
        icon: LineTicketIcon,
        message: {
          id: 'ISfGBY0rwClqd4bB8t7K',
          defaultMessage: 'Priority Check In & Boarding',
          description: 'Charter flight amenity: priority boarding in the flights flow',
        },
      },
    ],
    baggage: [
      {
        icon: LineBagIcon,
        message: {
          id: 'SBBJdZLWFogZDnxPBcZ6',
          defaultMessage: 'Carry-on 7kg',
          description: 'Charter flight standard carry-on baggage allowance',
        },
      },
      {
        icon: LineLuggageIcon,
        message: {
          id: 'dDAOzVfonIy_jZHM2Ry7',
          defaultMessage: 'Checked bag 23kg',
          description: 'Maldives charter flight standard checked baggage allowance',
        },
      },
    ],
  },
  [FlightsClassTypes.LUXROW]: {
    imageId: 'maldives-lux-row',
    inclusions: [
      {
        icon: LineSeatIcon,
        message: {
          id: 'IQUH3I0jStcnaaHV8YHY',
          defaultMessage: 'Enjoy more space for two passengers to unwind with a row of four economy seats',
          description: 'Charter flight SkyLounge amenity: extra space with four economy seats',
        },
      },
      {
        icon: LinePlayCircleIcon,
        message: {
          id: 'jOMZWuWfYpzHHZnLuRO7',
          defaultMessage: 'Stream on-demand entertainment to your personal device',
          description: 'Charter flight amenity: in-flight entertainment streaming to personal device',
        },
      },
      {
        icon: LineRestaurantIcon,
        message: {
          id: 'BQRK_i0lCZoPiyXIpz2F',
          defaultMessage: 'Complimentary meal and drinks service',
          description: 'Charter flight amenity: complimentary meal and drinks',
        },
      },
      {
        icon: LineTicketIcon,
        message: {
          id: 'QbpmHgAHGI-nie8wujID',
          defaultMessage: 'Priority Check In, Security & Boarding',
          description: 'Charter flight amenity: priority check in, security & boarding',
        },
      },
      {
        icon: LineDoorIcon,
        message: {
          id: '4c9FrzxtAUzvctMPi2aH',
          defaultMessage: 'Relax with airport lounge before your flight',
          description: 'Charter flight amenity: airport lounge access in the flights section',
        },
      },
    ],
    baggage: [
      {
        icon: LineBagIcon,
        message: {
          id: 'SBBJdZLWFogZDnxPBcZ6',
          defaultMessage: 'Carry-on 7kg',
          description: 'Charter flight standard carry-on baggage allowance',
        },
      },
      {
        icon: LineLuggageIcon,
        message: {
          id: 'dDAOzVfonIy_jZHM2Ry7',
          defaultMessage: 'Checked bag 23kg',
          description: 'Maldives charter flight standard checked baggage allowance',
        },
      },
    ],
  },
  [FlightsClassTypes.LUXROW_PAX_ONE]: {
    imageId: 'maldives-lux-row',
    inclusions: [
      {
        icon: LineSeatIcon,
        message: {
          id: 'charter.skylounge.solo.seat',
          defaultMessage: 'Enjoy more space to unwind with a row of four economy seats',
          description: 'Charter flight SkyLounge for one amenity: extra space solo variant',
        },
      },
      {
        icon: LinePlayCircleIcon,
        message: {
          id: 'jOMZWuWfYpzHHZnLuRO7',
          defaultMessage: 'Stream on-demand entertainment to your personal device',
          description: 'Charter flight amenity: in-flight entertainment streaming to personal device',
        },
      },
      {
        icon: LineRestaurantIcon,
        message: {
          id: 'BQRK_i0lCZoPiyXIpz2F',
          defaultMessage: 'Complimentary meal and drinks service',
          description: 'Charter flight amenity: complimentary meal and drinks',
        },
      },
      {
        icon: LineTicketIcon,
        message: {
          id: 'QbpmHgAHGI-nie8wujID',
          defaultMessage: 'Priority Check In, Security & Boarding',
          description: 'Charter flight amenity: priority check in, security & boarding',
        },
      },
      {
        icon: LineDoorIcon,
        message: {
          id: '4c9FrzxtAUzvctMPi2aH',
          defaultMessage: 'Relax with airport lounge before your flight',
          description: 'Charter flight amenity: airport lounge access in the flights section',
        },
      },
    ],
    baggage: [
      {
        icon: LineBagIcon,
        message: {
          id: 'SBBJdZLWFogZDnxPBcZ6',
          defaultMessage: 'Carry-on 7kg',
          description: 'Charter flight standard carry-on baggage allowance',
        },
      },
      {
        icon: LineLuggageIcon,
        message: {
          id: 'dDAOzVfonIy_jZHM2Ry7',
          defaultMessage: 'Checked bag 23kg',
          description: 'Maldives charter flight standard checked baggage allowance',
        },
      },
    ],
  },
  [FlightsClassTypes.BUSINESS]: {
    imageId: 'maldives-business',
    inclusions: [
      {
        icon: LineSeatIcon,
        message: {
          id: 'c0TjZdvglW0txcoTqdP0',
          defaultMessage: 'Luxury lie-flat seats with in-seat power for ultimate comfort and convenience',
          description: 'Charter flight business amenity: lie-flat seat description',
        },
      },
      {
        icon: LineCarIcon,
        message: {
          id: 'charter.business.transfer',
          defaultMessage: 'Home to airport transfers (max 40km) in MEL/SYD/BNE',
          description: 'Charter flight business amenity: home to airport transfer service',
        },
      },
      {
        icon: LinePlayCircleIcon,
        message: {
          id: 'MHWh67Ugh3N8s2fNT9jR',
          defaultMessage: 'Stream on-demand entertainment to provided handheld entertainment screens',
          description: 'Charter flight business amenity: entertainment on provided screens',
        },
      },
      {
        icon: LineRestaurantIcon,
        message: {
          id: '1BEZMl2YLOKp3GOUfM_T',
          defaultMessage: 'Gourmet dining options and drinks service',
          description: 'Charter flight business amenity: gourmet dining in the flights section',
        },
      },
      {
        icon: LineTicketIcon,
        message: {
          id: 'QbpmHgAHGI-nie8wujID',
          defaultMessage: 'Priority Check In, Security & Boarding',
          description: 'Charter flight amenity: priority check in, security & boarding',
        },
      },
      {
        icon: LineClochIcon,
        message: {
          id: '9E_wC1ftCC91UWs2b2B3',
          defaultMessage: 'Dedicated cabin crew for personalised service',
          description: 'Charter flight business amenity: dedicated cabin crew',
        },
      },
      {
        icon: LineDoorIcon,
        message: {
          id: '4c9FrzxtAUzvctMPi2aH',
          defaultMessage: 'Relax with airport lounge before your flight',
          description: 'Charter flight amenity: airport lounge access in the flights section',
        },
      },
    ],
    baggage: [
      {
        icon: LineBagIcon,
        message: {
          id: '3HU1zkF2KJRkfHNHGR4k',
          defaultMessage: 'Carry-on 2 pieces up to 14kg, max 10kg per piece',
          description: 'Charter flight business class carry-on baggage allowance',
        },
      },
      {
        icon: LineLuggageIcon,
        message: {
          id: 'nmBacWR105FebYmY5FEM',
          defaultMessage: 'Checked bag 2 pieces, total 46kg, no more than 32kg per piece',
          description: 'Charter flight business class checked baggage allowance',
        },
      },
    ],
  },
}

export { LineCarIcon }
